import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { apiRequest } from '../../api/client';
import { tagChildren } from '../../lib/tags';
import { whenText } from '../../lib/format';
import { AutoField, Check, Fieldset } from './fields';
import type { TagId } from '../../lib/types';

/**
 * WIDGET SCOPES AND THE KEYS UNDER THEM (V0.46) — the third tab of Users, Groups &
 * Permissions, behind the same `SHOW_USER_PERMISSIONS` gate as the other two.
 *
 * WHAT IS BEING EDITED, because it is not a user and not quite a role. A widget API key is a
 * credential another CONIGON application ships inside its own pages, and what it may read is
 * decided by the SCOPE it points at. Scope answers "what may be seen"; key answers "by whom,
 * from where, how often". They are separate entities on purpose — several keys legitimately
 * share one scope (production and staging, one per customer install, two live during a
 * rotation), and with the scope copied onto each key the failure mode is adding a tag to
 * production and forgetting staging.
 *
 * MASTER–DETAIL, AND DELIBERATELY THE ROLES TAB'S LAYOUT. `.role-split` / `.role-list` /
 * `.role-detail` / `.rolerow` / `.role-add` / `.role-pen` / `.role-bin` / `.role-foot` are
 * reused verbatim — not one line of CSS is added by this tab. That is the point: a third layout
 * idiom on one screen would be a third thing to learn, and this screen has established that
 * shape twice already. The name is edited in the LIST behind a pen, with a bin beside it, and
 * the detail pane holds exactly one scope at a time. The ids are new (`#wscope-list`,
 * `#wscope-detail`) because `#role-list` means the Roles tab to a spec that reads it.
 *
 * THREE ASYMMETRIES WITH THE ROLES TAB THAT ARE NOT ACCIDENTS, and each is stated in the UI
 * because getting one wrong is a content leak rather than an inconvenience:
 *
 *  - THE ROLE SUPPLIES HIDING ONLY. `auth/widgetScope.ts` hardcodes `roles: []`, so a scope
 *    borrows its Role's `hiddenNodes`/`hiddenTags` and DISCARDS its grants. Attaching a
 *    powerful Role to a scope grants the widget nothing.
 *  - THE TAG LIST IS AN ALLOW-LIST, so it fails CLOSED: nothing is in scope until a tag says
 *    so, and a node added to the map next month is out of scope by default. It is closed over
 *    the tag tree (the same `expandHiddenTags` the deny side uses), so choosing a parent tag
 *    admits its sub-tags — which is why a tag under a chosen one shows checked and disabled.
 *  - DOCUMENTATION KINDS FAIL CLOSED HERE. A Role's `docButtons` reads "absent means allowed";
 *    for a credential that answers unauthenticated cross-origin requests that is the wrong
 *    default, so an unticked kind here means NO.
 *
 * THE PREVIEW IS THE POINT OF THE TAB. `scopeTags: [CeSt, Public]` does not say which nodes it
 * admits, and the ancestor closure means the answer is not even "the tagged ones". So the
 * editor asks the server — `GET /api/admin/widget-scopes/:id/preview` — which resolves the
 * scope through the very machinery the widget goes through and reports the counts, the node
 * labels, the document titles, and what a given host URL would resolve to. There is no second
 * implementation of the scope walk or the path resolver anywhere on this screen.
 */

interface WidgetDocKinds {
  documentation?: boolean;
  internal?: boolean;
  course?: boolean;
  demo?: boolean;
  custom?: boolean;
}

interface WidgetKeyRow {
  id: string;
  name: string;
  scopeId: string;
  /** The first twelve characters of the plaintext — how a key is named in the UI and the logs. */
  keyPrefix: string;
  origins: string[];
  rateLimitPerMin: number;
  disabledAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

interface WidgetScopeRow {
  id: string;
  name: string;
  appTargetId: string | null;
  roleId: string | null;
  scopeTags: TagId[];
  docKinds: WidgetDocKinds;
  locales: string[] | null;
  aiChatEnabled: boolean;
  /** The ONE write the widget can make. Off by default; see the Reader feedback fieldset. */
  feedbackEnabled: boolean;
  keys: WidgetKeyRow[];
}

interface AppTargetRow {
  id: string;
  name: string;
}

interface PreviewNode {
  slug: string;
  label: string;
  docCount: number;
  contextOnly?: true;
}

interface PreviewDoc {
  slug: string;
  title: string;
  kind: string;
  docType: string;
}

interface PreviewProbe {
  asked: string;
  normalized: string;
  match: { kind: 'exact' | 'prefix' | 'none'; path: string | null };
  node: { slug: string; label: string; docs: { title: string; kind: string }[] } | null;
  parent: { slug: string; label: string; contextOnly?: true } | null;
  children: { slug: string; label: string; docs: { title: string }[]; contextOnly?: true }[];
  infoStationUrl: string | null;
}

interface Preview {
  scopeId: string;
  appTargetId: string | null;
  appTargetName: string | null;
  widgetApiEnabled: boolean;
  locale: string;
  locales: string[];
  defaultLocale: string;
  counts: {
    inScope: number;
    contextOnly: number;
    docs: number;
    routablePaths: number;
    totalNodes: number;
  };
  truncated: { nodes: boolean; docs: boolean };
  nodes: PreviewNode[];
  docs: PreviewDoc[];
  routablePaths: { path: string; slug: string; label: string }[];
  probe: PreviewProbe | null;
}

/** The five slots, in the order the band draws them. Kind names, not slot columns. */
const DOC_KIND_ROWS: { key: keyof WidgetDocKinds; label: string }[] = [
  { key: 'documentation', label: 'Documentation' },
  { key: 'internal', label: 'Internal Documentation' },
  { key: 'course', label: 'Course' },
  { key: 'demo', label: 'Demo' },
  { key: 'custom', label: 'Custom docs' },
];

const faint = { fontSize: 11.5, color: 'var(--ink-faint)' } as const;


export function WidgetKeysTab({ roles }: { roles: { id: string; name: string }[] }) {
  const s = useAppState();
  const [scopes, setScopes] = useState<WidgetScopeRow[]>([]);
  const [targets, setTargets] = useState<AppTargetRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  /** Held as an id and RESOLVED against the live list, exactly as the Roles tab does: nothing
   *  chosen resolves to the first scope, and a deleted one resolves to the first left. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = scopes.find((x) => x.id === selectedId) || scopes[0] || null;

  /** The row whose name is an input, mirrored in a ref — see the Roles tab's own note. */
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const editRef = useRef<{ id: string; text: string } | null>(null);
  const setEdit = (v: { id: string; text: string } | null) => {
    editRef.current = v;
    setEditing(v);
  };
  const cancelled = useRef(false);

  const load = useCallback(async () => {
    const [sc, tg] = await Promise.all([
      apiRequest<WidgetScopeRow[]>('/api/admin/widget-scopes'),
      apiRequest<AppTargetRow[]>('/api/admin/app-targets'),
    ]);
    setScopes(sc || []);
    setTargets(tg || []);
    setLoaded(true);
  }, []);

  useEffect(() => {
    void load().catch(() => setLoaded(true));
  }, [load]);

  const patchScope = async (id: string, body: Record<string, unknown>): Promise<boolean> => {
    try {
      const next = await apiRequest<WidgetScopeRow>(`/api/admin/widget-scopes/${id}`, {
        method: 'PUT',
        body,
      });
      setScopes((prev) => prev.map((x) => (x.id === id ? next : x)));
      return true;
    } catch {
      return false;
    }
  };

  const commitRename = async () => {
    const open = editRef.current;
    if (!open) return;
    setEdit(null);
    const scope = scopes.find((x) => x.id === open.id);
    if (!scope) return;
    const v = open.text.trim() || 'Scope';
    if (v === scope.name) return;
    if (!(await patchScope(open.id, { name: v }))) alert('Could not rename this scope.');
  };

  const addScope = async () => {
    try {
      const created = await apiRequest<WidgetScopeRow>('/api/admin/widget-scopes', {
        method: 'POST',
        // EMPTY allow-list, on purpose: a brand-new scope shows nothing at all until somebody
        // says what it may see. The other direction would publish the whole map on creation.
        body: { name: 'New scope', scopeTags: [], docKinds: { documentation: true } },
      });
      setScopes((prev) => [...prev, created]);
      setSelectedId(created.id);
    } catch {
      alert('Could not create this scope.');
    }
  };

  const removeScope = async (scope: WidgetScopeRow) => {
    const n = scope.keys.length;
    if (
      !confirm(
        n
          ? `"${scope.name}" has ${n} key${n === 1 ? '' : 's'}. Deleting the scope deletes ` +
              `${n === 1 ? 'it' : 'them'} too, and any application still using ${n === 1 ? 'it' : 'them'} ` +
              `stops receiving documentation immediately. Delete it?`
          : `Delete the scope "${scope.name}"?`,
      )
    ) {
      return;
    }
    try {
      await apiRequest(`/api/admin/widget-scopes/${scope.id}`, { method: 'DELETE' });
      setScopes((prev) => prev.filter((x) => x.id !== scope.id));
    } catch {
      alert('Could not delete this scope.');
    }
  };

  return (
    <>
      <div className="perm-section-lead">
        A <b>scope</b> says what an application may read: a tag allow-list, a Role for the
        branches to hide inside it, which documentation kinds and which languages. A <b>key</b>
        is one credential pointing at a scope — issued, rate-limited, rotated and revoked on its
        own, so several applications or environments can share one scope safely. Pick a scope on
        the left; rename it with the pen and delete it with the bin.
      </div>

      <div className="role-split">
        <div id="wscope-list" className="role-list">
          <button id="wscope-add" className="lbtn role-add" onClick={addScope}>
            <svg className="ico" style={{ width: 14, height: 14 }}>
              <use href="#i-plus" />
            </svg>
            Add scope
          </button>

          {!loaded ? (
            <div className="pick-empty">Loading…</div>
          ) : !scopes.length ? (
            <div className="pick-empty">No scopes yet — add one above.</div>
          ) : null}

          {scopes.map((scope) => (
            <div key={scope.id}>
              <div
                className={selected && scope.id === selected.id ? 'rolerow sel' : 'rolerow'}
                data-scope={scope.id}
              >
                {editing && editing.id === scope.id ? (
                  <input
                    className="fld rolerow-edit"
                    aria-label="Scope name"
                    autoFocus
                    value={editing.text}
                    onFocus={(e) => e.currentTarget.select()}
                    onChange={(e) => setEdit({ id: scope.id, text: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void commitRename();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelled.current = true;
                        setEdit(null);
                      }
                    }}
                    onBlur={() => {
                      if (cancelled.current) {
                        cancelled.current = false;
                        return;
                      }
                      void commitRename();
                    }}
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      className="rolerow-name"
                      title={`Open ${scope.name}`}
                      onClick={() => setSelectedId(scope.id)}
                    >
                      {scope.name}
                    </button>
                    <button
                      type="button"
                      className="role-pen"
                      aria-label="Edit"
                      title={`Rename ${scope.name}`}
                      onClick={() => {
                        cancelled.current = false;
                        setEdit({ id: scope.id, text: scope.name });
                      }}
                    >
                      <svg className="ico">
                        <use href="#i-edit" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="role-bin"
                      aria-label="Delete"
                      title={`Delete ${scope.name}`}
                      onClick={() => void removeScope(scope)}
                    >
                      <svg className="ico">
                        <use href="#i-trash" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
              {/* THE KEYS, UNDER THEIR SCOPE — the shape the brief asks for, and read-only
                  here. It answers "which credentials does this scope actually carry?" without
                  opening it, which is the question somebody chasing a leaked key has. The
                  editable rows live in the detail pane, where there is room for an origin. */}
              {scope.keys.length ? (
                <div style={{ margin: '1px 0 5px 12px', display: 'grid', gap: 2 }}>
                  {scope.keys.map((k) => (
                    <div
                      key={k.id}
                      data-key={k.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11.5,
                        color: k.disabledAt ? 'var(--ink-faint)' : 'var(--ink-dim)',
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          flex: '0 0 auto',
                          background: k.disabledAt
                            ? 'var(--status-doc-rejected)'
                            : 'var(--review-approved)',
                        }}
                      />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {k.name}
                      </span>
                      {k.disabledAt ? <span style={faint}>revoked</span> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div id="wscope-detail" className="role-detail">
          {selected ? (
            <ScopeCard
              key={selected.id}
              scope={selected}
              roles={roles}
              targets={targets}
              languages={s.languages.map((l) => l.code)}
              tags={s.tags}
              tagEdges={s.tagEdges}
              patchScope={patchScope}
              onKeysChanged={load}
            />
          ) : (
            <div className="pick-empty">
              {loaded ? 'No scopes yet — add one on the left.' : 'Loading…'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- the open scope */

function ScopeCard({
  scope,
  roles,
  targets,
  languages,
  tags,
  tagEdges,
  patchScope,
  onKeysChanged,
}: {
  scope: WidgetScopeRow;
  roles: { id: string; name: string }[];
  targets: AppTargetRow[];
  languages: string[];
  tags: ReturnType<typeof useAppState>['tags'];
  tagEdges: ReturnType<typeof useAppState>['tagEdges'];
  patchScope: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  onKeysChanged: () => Promise<void>;
}) {
  const docKinds = scope.docKinds || {};
  const allLanguages = scope.locales === null;

  const toggleDocKind = async (key: keyof WidgetDocKinds, on: boolean) => {
    if (!(await patchScope(scope.id, { docKinds: { ...docKinds, [key]: on } }))) {
      alert('Could not update the documentation kinds for this scope.');
    }
  };

  const toggleLocale = async (code: string, on: boolean) => {
    const cur = scope.locales ?? languages;
    const next = on ? [...new Set([...cur, code])] : cur.filter((c) => c !== code);
    // Emptying the list would be indistinguishable from "every language" on the wire, so the
    // last one cannot be unticked — "serve every language" is the way to say that.
    if (!next.length) return alert('A scope must serve at least one language, or every language.');
    if (!(await patchScope(scope.id, { locales: next }))) {
      alert('Could not update the languages for this scope.');
    }
  };

  return (
    <>
      <div className="card">
        <h3>
          <span className="role-title">{scope.name}</span>
          <span className="grow" />
          <span style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: 'var(--font)', fontWeight: 400 }}>
            {scope.keys.length} key{scope.keys.length === 1 ? '' : 's'}
          </span>
        </h3>

        <div className="perm-grp-body">
          <Fieldset title="Application">
            <select
              className="fld"
              id="wscope-target"
              value={scope.appTargetId || ''}
              onChange={async (e) => {
                if (!(await patchScope(scope.id, { appTargetId: e.target.value || null }))) {
                  alert("Could not update this scope's application.");
                }
              }}
            >
              <option value="">— no application —</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <div style={{ ...faint, marginTop: 6 }}>
              {scope.appTargetId
                ? 'A host URL is resolved against the documentation locations set on this ' +
                  "application's nodes. `/users` means different documentation in each " +
                  'application, which is why the scope has to name one.'
                : 'Without an application this scope resolves no URL at all — the widget will ' +
                  'answer every page with “no documentation for this page”.'}
            </div>
          </Fieldset>

          <Fieldset title="Role — hidden branches and tags only">
            <select
              className="fld"
              id="wscope-role"
              value={scope.roleId || ''}
              onChange={async (e) => {
                if (!(await patchScope(scope.id, { roleId: e.target.value || null }))) {
                  alert("Could not update this scope's role.");
                }
              }}
            >
              <option value="">— no role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <div style={{ ...faint, marginTop: 6 }}>
              Only the role’s <b>hidden branches</b> and <b>hidden tags</b> are borrowed. Its
              permissions are discarded — a key holding a role that can edit the map still
              cannot write anything, ever.
            </div>
          </Fieldset>

          <Fieldset title="Content in scope — tag allow-list">
            <div style={{ margin: '-4px 0 8px', color: 'var(--ink-faint)', fontSize: 11.5 }}>
              Only content carrying a checked tag is in scope, sub-tags included. Nothing is in
              scope until a tag says so, so a node added to the map later stays private until
              somebody tags it.
            </div>
            <div className="pick-box">
              <ScopeTagPicker scope={scope} patchScope={patchScope} tags={tags} tagEdges={tagEdges} />
            </div>
          </Fieldset>

          <Fieldset title="Documentation kinds">
            <div style={{ margin: '-4px 0 8px', color: 'var(--ink-faint)', fontSize: 11.5 }}>
              Unticked means <b>no</b>, unlike a role’s documentation buttons: this credential
              answers unauthenticated requests, so it is closed until it is opened.
            </div>
            {DOC_KIND_ROWS.map((d) => (
              <Check
                key={d.key}
                label={d.label}
                checked={docKinds[d.key] === true}
                onChange={(v) => void toggleDocKind(d.key, v)}
              />
            ))}
          </Fieldset>

          <Fieldset title="Languages">
            <Check
              label="Serve every language"
              sub="Follows the deployment — a language added later is served without editing this scope"
              checked={allLanguages}
              onChange={async (v) => {
                const body = { locales: v ? null : languages.slice(0, 1) };
                if (!(await patchScope(scope.id, body))) {
                  alert('Could not update the languages for this scope.');
                }
              }}
            />
            {allLanguages
              ? null
              : languages.map((code) => (
                  <Check
                    key={code}
                    label={code}
                    checked={(scope.locales ?? []).includes(code)}
                    onChange={(v) => void toggleLocale(code, v)}
                  />
                ))}
          </Fieldset>

          <Fieldset title="AI Help chat">
            <Check
              label="Allow scoped AI Help through this scope"
              sub="Reserved for v2 — no chat route answers a key yet, and an air-gapped deployment offers none whatever this says"
              checked={scope.aiChatEnabled}
              onChange={async (v) => {
                if (!(await patchScope(scope.id, { aiChatEnabled: v }))) {
                  alert('Could not update the AI Help setting for this scope.');
                }
              }}
            />
          </Fieldset>

          {/* THE ONLY WRITE THIS SURFACE HAS, and the checkbox says what turning it on means.
              Every other widget route is a read; this one accepts free text from anybody who
              can load a host page, which is a decision and not a setting. */}
          <Fieldset title="Reader feedback">
            <Check
              id="wscope-feedback"
              label="Let readers rate a page and report a problem"
              sub="The only thing the widget can WRITE. Readers are not signed in, so a report stores the host path, the language, a rating and optional text — and no identity of any kind. Off unless you turn it on."
              checked={scope.feedbackEnabled}
              onChange={async (v) => {
                if (!(await patchScope(scope.id, { feedbackEnabled: v }))) {
                  alert('Could not update the feedback setting for this scope.');
                }
              }}
            />
          </Fieldset>
        </div>
      </div>

      <KeysCard scope={scope} onKeysChanged={onKeysChanged} />
      <ScopePreview scope={scope} />
      <WidgetFeedbackCard scope={scope} />
    </>
  );
}

/**
 * The tag ALLOW-list picker. The hidden-tag picker's shape, opposite polarity.
 *
 * A tag under a checked one shows checked AND disabled, because the server closes `scopeTags`
 * over the tag tree (`expandHiddenTags`, polarity-blind, one implementation) — so it IS in
 * scope, and unchecking it here would promise something this control cannot deliver. The same
 * rule and the same reason as the hidden pickers on the Roles tab.
 */
function ScopeTagPicker({
  scope,
  patchScope,
  tags,
  tagEdges,
}: {
  scope: WidgetScopeRow;
  patchScope: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  tags: ReturnType<typeof useAppState>['tags'];
  tagEdges: ReturnType<typeof useAppState>['tagEdges'];
}) {
  const roots = tagChildren(tags, tagEdges, null);
  if (!roots.length) return <div className="pick-empty">No tags defined.</div>;

  const chosen = new Set(scope.scopeTags || []);
  const ancestorChosen = (id: TagId): boolean => {
    let p = tagEdges[id];
    while (p !== undefined) {
      if (chosen.has(p)) return true;
      p = tagEdges[p];
    }
    return false;
  };

  const toggle = async (id: TagId, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    if (!(await patchScope(scope.id, { scopeTags: [...next] }))) {
      alert('Could not update the tag allow-list.');
    }
  };

  const rows: React.ReactNode[] = [];
  const walk = (id: TagId, depth: number) => {
    const t = tags[id];
    if (!t) return;
    const anc = ancestorChosen(id);
    rows.push(
      <div key={id} className={anc ? 'pick-row pk-off' : 'pick-row'} style={{ paddingLeft: 4 + depth * 15 }}>
        <input
          type="checkbox"
          data-scopetag={id}
          checked={chosen.has(id) || anc}
          disabled={anc}
          onChange={(e) => void toggle(id, e.target.checked)}
        />
        <span className="pk-dot" style={{ background: t.color }} />
        <span>{t.name}</span>
      </div>,
    );
    for (const k of tagChildren(tags, tagEdges, id)) walk(k, depth + 1);
  };
  for (const r of roots) walk(r, 0);

  return <>{rows}</>;
}

/* ----------------------------------------------------------------------- the keys */

/**
 * The keys on this scope: issue, origins, rate limit, rotate, revoke, last used.
 *
 * THE PLAINTEXT EXISTS ONCE. `POST /api/admin/api-keys` and the rotate route are the only two
 * responses in this server that carry it, and there is no route that COULD return it again —
 * the table holds a sha256 and a twelve-character prefix. So the reveal below is deliberately
 * hard to miss and deliberately hard to dismiss by accident, and it says plainly what a key is:
 * a value that ships inside another application's pages, readable by anyone who can load one.
 */
function KeysCard({
  scope,
  onKeysChanged,
}: {
  scope: WidgetScopeRow;
  onKeysChanged: () => Promise<void>;
}) {
  const [issuing, setIssuing] = useState(false);
  const [form, setForm] = useState({ name: '', origins: '', rateLimitPerMin: '120' });
  /** The one and only time this value is on screen. `null` the rest of the time. */
  const [revealed, setRevealed] = useState<{ name: string; key: string; rotated: boolean } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const originsOf = (raw: string): string[] =>
    raw
      .split(/[\s,]+/)
      .map((v) => v.trim())
      .filter(Boolean);

  const issue = async () => {
    const name = form.name.trim();
    if (!name) return alert('Give this key a name — it is how you will recognise it later.');
    const origins = originsOf(form.origins);
    if (!origins.length) {
      return alert('List at least one origin, e.g. https://cest.example.com — “*” is refused.');
    }
    const rate = parseInt(form.rateLimitPerMin, 10);
    setBusy(true);
    try {
      const created = await apiRequest<WidgetKeyRow & { key: string }>('/api/admin/api-keys', {
        method: 'POST',
        body: {
          name,
          scopeId: scope.id,
          origins,
          ...(Number.isFinite(rate) && rate > 0 ? { rateLimitPerMin: rate } : {}),
        },
      });
      setRevealed({ name: created.name, key: created.key, rotated: false });
      setForm({ name: '', origins: '', rateLimitPerMin: '120' });
      setIssuing(false);
      await onKeysChanged();
    } catch (e) {
      const msg = (e as { body?: { error?: string } } | null)?.body?.error;
      alert(msg || 'Could not issue this key.');
    } finally {
      setBusy(false);
    }
  };

  const patchKey = async (id: string, body: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiRequest(`/api/admin/api-keys/${id}`, { method: 'PUT', body });
      await onKeysChanged();
      return true;
    } catch {
      return false;
    }
  };

  const rotate = async (k: WidgetKeyRow) => {
    if (
      !confirm(
        `Rotate "${k.name}"? The value currently deployed stops working immediately — there is ` +
          `no overlap. Everything else about the key is kept: its name, its origins, its rate ` +
          `limit. If you need two working credentials during a changeover, issue a second key ` +
          `on this scope instead and revoke the old one afterwards.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const next = await apiRequest<WidgetKeyRow & { key: string }>(
        `/api/admin/api-keys/${k.id}/rotate`,
        { method: 'POST' },
      );
      setRevealed({ name: next.name, key: next.key, rotated: true });
      await onKeysChanged();
    } catch {
      alert('Could not rotate this key.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (k: WidgetKeyRow) => {
    if (
      !confirm(
        `Delete "${k.name}" (${k.keyPrefix}…)? Revoking is usually what you want — it stops the ` +
          `key while keeping the row, so you can see it was there. Deleting removes the record.`,
      )
    ) {
      return;
    }
    try {
      await apiRequest(`/api/admin/api-keys/${k.id}`, { method: 'DELETE' });
      await onKeysChanged();
    } catch {
      alert('Could not delete this key.');
    }
  };

  return (
    <div className="card" id="wkeys-card">
      <h3>
        Keys
        <span className="grow" />
        <button className="lbtn" id="wkey-add" onClick={() => setIssuing((v) => !v)}>
          <svg className="ico" style={{ width: 14, height: 14 }}>
            <use href="#i-plus" />
          </svg>
          Issue a key
        </button>
      </h3>

      {revealed ? (
        <div
          id="wkey-revealed"
          style={{
            border: '1px solid var(--status-needs-update)',
            background: 'color-mix(in srgb, var(--status-needs-update) 12%, transparent)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
            {revealed.rotated ? 'New value for' : 'Issued'} “{revealed.name}” — copy it now
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-dim)', marginBottom: 9, lineHeight: 1.55 }}>
            This is the <b>only</b> time this value is shown. Nothing stores it — the server
            keeps a hash and the first twelve characters — so if it is lost the only remedy is to
            rotate the key and deploy the new value.
          </div>
          <input
            className="fld"
            id="wkey-plaintext"
            readOnly
            value={revealed.key}
            onFocus={(e) => e.currentTarget.select()}
            style={{ fontFamily: 'ui-monospace,Consolas,monospace', fontSize: 12.5 }}
          />
          <div style={{ display: 'flex', gap: 9, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              className="lbtn"
              onClick={() => {
                void navigator.clipboard?.writeText(revealed.key).catch(() => {});
              }}
            >
              Copy
            </button>
            <button className="lbtn" id="wkey-revealed-done" onClick={() => setRevealed(null)}>
              I have copied it
            </button>
          </div>
        </div>
      ) : null}

      {issuing ? (
        <div
          id="wkey-form"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          {/* THE HONEST SENTENCE, and the plan asks for it in as many words. A widget key
              travels inside a host application's HTML: it is not a secret, it is a scoped,
              revocable, rate-limited public identifier, and an admin who believes otherwise
              will put content behind it that should not be there. */}
          <div
            style={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: 'var(--ink)',
              borderLeft: '3px solid var(--status-needs-update)',
              paddingLeft: 10,
              marginBottom: 12,
            }}
          >
            <b>This key will be readable by anyone who can load the host page.</b> It ships inside
            another application’s pages, so treat it as public: the origin list, the rate limit
            and one-click revocation are the protection, not secrecy. Never give a scope content
            that must not leave this deployment.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <input
              className="fld"
              id="wkey-name"
              placeholder="name, e.g. CeSt production"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <input
              className="fld"
              id="wkey-origins"
              placeholder="https://cest.example.com"
              value={form.origins}
              onChange={(e) => setForm((f) => ({ ...f, origins: e.target.value }))}
            />
            <input
              className="fld"
              id="wkey-rate"
              inputMode="numeric"
              placeholder="requests per minute"
              value={form.rateLimitPerMin}
              onChange={(e) => setForm((f) => ({ ...f, rateLimitPerMin: e.target.value }))}
            />
          </div>
          <div style={{ ...faint, marginTop: 7 }}>
            One origin per line or comma-separated, each a bare origin with no path. “*” is
            refused: with a credential that ships in a page, a wildcard makes the list a
            decoration.
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <button
              className="lbtn"
              id="wkey-issue"
              disabled={busy}
              style={{ background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}
              onClick={() => void issue()}
            >
              Issue key
            </button>
            <button className="lbtn" onClick={() => setIssuing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {!scope.keys.length ? (
        <div className="pick-empty">
          No keys on this scope yet. Nothing can read it until one is issued.
        </div>
      ) : (
        <table className="utable">
          <thead>
            <tr>
              <th>Key</th>
              <th>Allowed origins</th>
              <th>Per minute</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scope.keys.map((k) => (
              <tr key={k.id} data-key={k.id} style={k.disabledAt ? { opacity: 0.6 } : undefined}>
                <td>
                  <AutoField
                    value={k.name}
                    onSave={async (raw) => {
                      const v = raw.trim();
                      if (!v || v === k.name) return k.name;
                      const ok = await patchKey(k.id, { name: v });
                      if (!ok) alert('Could not rename this key.');
                      return ok ? null : k.name;
                    }}
                  />
                  <div style={{ ...faint, marginTop: 4, fontFamily: 'ui-monospace,Consolas,monospace' }}>
                    {k.keyPrefix}…
                    {k.disabledAt ? (
                      <span style={{ color: 'var(--status-doc-rejected)' }}> · revoked</span>
                    ) : null}
                  </div>
                </td>
                <td>
                  <AutoField
                    value={k.origins.join(', ')}
                    title="Bare origins, comma-separated. An abuse limiter, not authentication — Origin can be spoofed."
                    onSave={async (raw) => {
                      const next = originsOf(raw);
                      if (!next.length) {
                        alert('A key needs at least one origin.');
                        return k.origins.join(', ');
                      }
                      const ok = await patchKey(k.id, { origins: next });
                      if (!ok) {
                        alert('Could not update the origins — each must be a bare origin, and “*” is refused.');
                        return k.origins.join(', ');
                      }
                      return null;
                    }}
                  />
                </td>
                <td>
                  <AutoField
                    value={String(k.rateLimitPerMin)}
                    onSave={async (raw) => {
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n) || n <= 0) return String(k.rateLimitPerMin);
                      const ok = await patchKey(k.id, { rateLimitPerMin: n });
                      if (!ok) alert('Could not update the rate limit.');
                      return ok ? null : String(k.rateLimitPerMin);
                    }}
                  />
                </td>
                <td style={{ fontSize: 12, color: 'var(--ink-dim)' }}>{whenText(k.lastUsedAt)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    className="pbtn"
                    disabled={busy}
                    title="Give this key a new value. The deployed one stops working at once."
                    onClick={() => void rotate(k)}
                  >
                    Rotate
                  </button>{' '}
                  <button
                    className="pbtn"
                    title={
                      k.disabledAt
                        ? 'Put this key back into service'
                        : 'Stop this key. Its siblings on this scope keep working.'
                    }
                    onClick={async () => {
                      if (!(await patchKey(k.id, { disabled: !k.disabledAt }))) {
                        alert('Could not change this key’s state.');
                      }
                    }}
                  >
                    {k.disabledAt ? 'Restore' : 'Revoke'}
                  </button>{' '}
                  <button className="icon-x" title="Delete key" onClick={() => void remove(k)}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- the preview */

/**
 * WHAT THIS SCOPE CAN SEE, from the server, computed by the machinery the widget itself goes
 * through. Read-only, and the one place a tag allow-list becomes legible.
 *
 * It refetches whenever the scope's own fields change — every control above writes immediately,
 * so `scope` is the version the server holds and using it as the dependency is what makes
 * ticking a tag update the counts without a Refresh button. The probe is separate, because
 * typing a URL should not re-run on every keystroke.
 */
function ScopePreview({ scope }: { scope: WidgetScopeRow }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState(false);
  const [probeText, setProbeText] = useState('');
  const [probing, setProbing] = useState(false);
  /** What the last fetch asked for, so a probe survives a refetch caused by an edit. */
  const probePath = useRef('');

  const fingerprint = JSON.stringify([
    scope.appTargetId,
    scope.roleId,
    [...scope.scopeTags].sort(),
    scope.docKinds,
    scope.locales,
  ]);

  const fetchPreview = useCallback(
    async (path: string) => {
      probePath.current = path;
      try {
        const p = await apiRequest<Preview>(`/api/admin/widget-scopes/${scope.id}/preview`, {
          query: path ? { path } : undefined,
        });
        setPreview(p);
        setError(false);
      } catch {
        setError(true);
      }
    },
    [scope.id],
  );

  useEffect(() => {
    void fetchPreview(probePath.current);
    // The fingerprint, not the whole object: a key being renamed must not re-run the walk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPreview, fingerprint]);

  const runProbe = async () => {
    setProbing(true);
    await fetchPreview(probeText.trim());
    setProbing(false);
  };

  return (
    <div className="card" id="wscope-preview">
      <h3>
        What this scope can see
        <span className="grow" />
        {preview && !preview.widgetApiEnabled ? (
          <span style={{ fontSize: 12, color: 'var(--status-needs-update)', fontWeight: 400 }}>
            the widget API is off on this deployment
          </span>
        ) : null}
      </h3>

      {error ? (
        <div className="pick-empty">Could not work out what this scope resolves to.</div>
      ) : !preview ? (
        <div className="pick-empty">Working it out…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 12 }}>
            <Stat n={preview.counts.inScope} label={`of ${preview.counts.totalNodes} nodes in scope`} />
            <Stat n={preview.counts.docs} label="documents readable" />
            <Stat n={preview.counts.routablePaths} label="host addresses resolvable" />
            <Stat
              n={preview.counts.contextOnly}
              label="ancestors for context only"
              hint="Present so the trail stays connected. They show a label and serve no documents."
            />
          </div>

          {!preview.counts.inScope ? (
            <div style={{ ...faint, marginBottom: 12 }}>
              Nothing is in scope, so this scope shows nothing at all. Tick a tag above.
            </div>
          ) : null}

          {/* THE PROBE. A host application's URL in, and what the widget would draw out. */}
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <input
              className="fld"
              id="wscope-probe"
              style={{ flex: '1 1 220px', minWidth: 160 }}
              placeholder="Try a host URL, e.g. /users/123"
              value={probeText}
              onChange={(e) => setProbeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void runProbe();
                }
              }}
            />
            <button className="lbtn" id="wscope-probe-go" disabled={probing} onClick={() => void runProbe()}>
              Resolve
            </button>
            {preview.probe ? (
              <button
                className="lbtn"
                onClick={() => {
                  setProbeText('');
                  void fetchPreview('');
                }}
              >
                Clear
              </button>
            ) : null}
          </div>

          {preview.probe ? (
            <div
              id="wscope-probe-result"
              style={{
                border: '1px solid var(--line)',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 14,
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            >
              {preview.probe.match.kind === 'none' ? (
                <>
                  <b>Nothing documents {preview.probe.normalized}.</b> The widget draws its “no
                  documentation for this page” state — an expected state, not an error.
                  {preview.appTargetId
                    ? null
                    : ' This scope has no application, so no URL can resolve at all.'}
                </>
              ) : (
                <>
                  <div>
                    <b>{preview.probe.node?.label}</b>{' '}
                    <span style={faint}>
                      {preview.probe.match.kind === 'exact'
                        ? `exact match on ${preview.probe.match.path}`
                        : `longest prefix ${preview.probe.match.path} matched ${preview.probe.normalized}`}
                    </span>
                  </div>
                  <BandLine label="Parent" nodes={preview.probe.parent ? [preview.probe.parent] : []} />
                  <div style={{ marginTop: 3 }}>
                    Documents:{' '}
                    {preview.probe.node?.docs.length
                      ? preview.probe.node.docs.map((d) => d.title).join(', ')
                      : 'none on this node'}
                  </div>
                  <BandLine label="Children" nodes={preview.probe.children} />
                  {preview.probe.infoStationUrl ? (
                    <div style={{ ...faint, marginTop: 3, wordBreak: 'break-all' }}>
                      Open in InfoStation: {preview.probe.infoStationUrl}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          <div className="perm-grp-body">
            <Fieldset title={`Nodes in scope (${preview.counts.inScope + preview.counts.contextOnly})`}>
              <div className="pick-box">
                {preview.nodes.length ? (
                  preview.nodes.map((n) => (
                    <div key={n.slug} className={n.contextOnly ? 'pick-row pk-off' : 'pick-row'}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {n.label}
                      </span>
                      <span style={faint}>
                        {n.contextOnly
                          ? 'context only'
                          : `${n.docCount} doc${n.docCount === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="pick-empty">Nothing.</div>
                )}
                {preview.truncated.nodes ? <div style={faint}>…and more, not listed.</div> : null}
              </div>
            </Fieldset>

            <Fieldset title={`Documents readable (${preview.counts.docs})`}>
              <div className="pick-box">
                {preview.docs.length ? (
                  preview.docs.map((d) => (
                    <div key={d.slug} className="pick-row">
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.title}
                      </span>
                      <span style={faint}>{d.kind}</span>
                    </div>
                  ))
                ) : (
                  <div className="pick-empty">None.</div>
                )}
                {preview.truncated.docs ? <div style={faint}>…and more, not listed.</div> : null}
              </div>
            </Fieldset>

            <Fieldset title={`Host addresses this scope resolves (${preview.counts.routablePaths})`}>
              <div className="pick-box">
                {preview.routablePaths.length ? (
                  preview.routablePaths.map((r) => (
                    <div key={r.path} className="pick-row">
                      <span style={{ fontFamily: 'ui-monospace,Consolas,monospace', flex: '0 0 auto' }}>
                        {r.path}
                      </span>
                      <span style={{ ...faint, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
                    </div>
                  ))
                ) : (
                  <div className="pick-empty">
                    None yet. A node needs a “Documentation location in the app” — set it in the
                    node’s panel — and this scope needs to name the same application.
                  </div>
                )}
              </div>
            </Fieldset>

            <Fieldset title="Languages served">
              <div style={{ fontSize: 12.5, color: 'var(--ink-dim)' }}>
                {preview.locales.join(', ')} — default {preview.defaultLocale}
              </div>
            </Fieldset>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ n, label, hint }: { n: number; label: string; hint?: string }) {
  return (
    <div title={hint} style={{ minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{n}</div>
      <div style={faint}>{label}</div>
    </div>
  );
}

/** One line of the band the widget would draw. Context-only relatives are marked as such. */
function BandLine({
  label,
  nodes,
}: {
  label: string;
  nodes: { slug: string; label: string; contextOnly?: true }[];
}) {
  return (
    <div style={{ marginTop: 3 }}>
      {label}:{' '}
      {nodes.length ? (
        nodes.map((n, i) => (
          <span key={n.slug}>
            {i ? ', ' : ''}
            {n.label}
            {n.contextOnly ? <span style={faint}> (context only)</span> : null}
          </span>
        ))
      ) : (
        <span style={faint}>none in scope</span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------
 * WHAT READERS SAID (V0.46 W6) — the other end of the one write the widget can make.
 * ------------------------------------------------------------------------------------------- */

interface FeedbackRow {
  id: string;
  scopeId: string;
  hostPath: string;
  matchKind: 'exact' | 'prefix' | 'none';
  nodeSlug: string | null;
  docSlug: string | null;
  locale: string;
  rating: 'helpful' | 'not-helpful';
  reason: string | null;
  comment: string | null;
  status: 'new' | 'accepted' | 'dismissed';
  handledAt: string | null;
  createdAt: string;
  apiKey: { name: string; keyPrefix: string } | null;
}

interface FeedbackPayload {
  scopeId: string | null;
  counts: { total: number; new: number; helpful: number; notHelpful: number; unmapped: number };
  gaps: { hostPath: string; count: number; lastAt: string | null }[];
  truncated: boolean;
  rows: FeedbackRow[];
}

/** The reasons, as a sentence rather than an enum. The values are the contract's. */
const REASON_WORDS: Record<string, string> = {
  'out-of-date': 'out of date',
  wrong: 'something is wrong',
  incomplete: 'missing what they needed',
  unclear: 'could not follow it',
  'wrong-page': 'not about this page',
  other: 'something else',
};

/**
 * WHAT READERS SAID — reports from the host application, and the screens nothing answered.
 *
 * WHY THIS IS ON THIS TAB rather than in the dashboard's Documents tab, which is where the
 * feedback handoff suggested it. What arrives here is unauthenticated free text from strangers
 * on the public internet: unfiltered, possibly naming people, possibly abuse, and with no
 * author-level control over who sends it. Reading that is a different act from reading
 * documentation, and the people who decided this application may write to this installation at
 * all — who own the scope, its keys and the flag that opened the surface — are the right first
 * readers of what came through it. It also keeps the gate honest: these rows are behind
 * SHOW_USER_PERMISSIONS because they are on this page, not because a second gate was
 * remembered. A per-document tally for authors is a separate, later thing, and it belongs where
 * the handoff put it.
 *
 * THE COMMENT IS RENDERED AS TEXT, and that is the load-bearing line in this file. It is
 * attacker-controlled by construction, stored verbatim, and reaches the DOM only as React text
 * content — never dangerouslySetInnerHTML, never an href, never anything that parses. Spec 58
 * drives this panel with a comment full of markup and asserts the DOM shows the characters.
 *
 * THE GAP LIST IS THE VALUABLE HALF: reports whose path resolved to nothing had no document to
 * be about, because there was none. Ranked by count, that is the backlog which should drive the
 * docPath mapping and the writing after it — which is why it is a section of its own and not
 * something buried inside a per-document view it could not be inside.
 */
function WidgetFeedbackCard({ scope }: { scope: WidgetScopeRow }) {
  const [data, setData] = useState<FeedbackPayload | null>(null);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<'new' | 'accepted' | 'dismissed' | 'all'>('new');

  const load = useCallback(async () => {
    try {
      const p = await apiRequest<FeedbackPayload>('/api/admin/widget-feedback', {
        query: { scopeId: scope.id, ...(status === 'all' ? {} : { status }) },
      });
      setData(p);
      setError(false);
    } catch {
      setError(true);
    }
  }, [scope.id, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const setRowStatus = async (row: FeedbackRow, next: FeedbackRow['status']) => {
    try {
      await apiRequest(`/api/admin/widget-feedback/${row.id}`, { method: 'PUT', body: { status: next } });
      await load();
    } catch {
      alert('Could not update this report.');
    }
  };

  const remove = async (row: FeedbackRow) => {
    // The retention control, and the only one: nothing here expires on its own, which is what
    // PRIVACY.md says and therefore what has to be possible.
    if (!confirm('Delete this report? Nothing else keeps a copy of it.')) return;
    try {
      await apiRequest(`/api/admin/widget-feedback/${row.id}`, { method: 'DELETE' });
      await load();
    } catch {
      alert('Could not delete this report.');
    }
  };

  return (
    <div className="card" id="wscope-feedback-card">
      <h3>
        What readers said
        <span className="grow" />
        {!scope.feedbackEnabled ? (
          <span style={{ fontSize: 12, color: 'var(--ink-faint)', fontWeight: 400 }}>
            switched off — nothing new can arrive
          </span>
        ) : null}
      </h3>

      {error ? (
        <div className="pick-empty">Could not load the reports for this scope.</div>
      ) : !data ? (
        <div className="pick-empty">Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 12 }}>
            <Stat n={data.counts.total} label="reports in all" />
            <Stat n={data.counts.new} label="not yet dealt with" />
            <Stat n={data.counts.helpful} label="said it helped" />
            <Stat n={data.counts.notHelpful} label="said it did not" />
            <Stat
              n={data.counts.unmapped}
              label="on screens nothing documents"
              hint="The path resolved to nothing, so there was no page to rate. These are the gaps below."
            />
          </div>

          {/* THE GAP LIST. Ranked by how many people looked and found nothing. */}
          {data.gaps.length ? (
            <Fieldset title={`Screens people looked for help on and found none (${data.gaps.length})`}>
              <div id="wscope-feedback-gaps">
                {data.gaps.map((g) => (
                  <div key={g.hostPath} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{g.hostPath}</span>
                    <span style={faint}>
                      {g.count} {g.count === 1 ? 'report' : 'reports'}, last {whenText(g.lastAt)}
                    </span>
                  </div>
                ))}
              </div>
            </Fieldset>
          ) : null}

          <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 6 }}>
            <span style={faint}>Showing</span>
            <select
              className="fld"
              id="wscope-feedback-filter"
              aria-label="Which reports to show"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              style={{ width: 'auto' }}
            >
              <option value="new">Not yet dealt with</option>
              <option value="accepted">Accepted</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">Everything</option>
            </select>
          </div>

          {!data.rows.length ? (
            <div className="pick-empty" id="wscope-feedback-empty">
              {data.counts.total ? 'Nothing under this filter.' : 'No reports yet.'}
            </div>
          ) : (
            <div id="wscope-feedback-rows">
              {data.rows.map((row) => (
                <div
                  key={row.id}
                  data-feedback={row.id}
                  style={{
                    borderTop: '1px solid var(--line)',
                    padding: '7px 0',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                  }}
                >
                  <span
                    title={row.rating === 'helpful' ? 'Said it helped' : 'Said it did not help'}
                    style={{ fontWeight: 600 }}
                  >
                    {row.rating === 'helpful' ? 'Yes' : 'No'}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {row.hostPath}
                      {row.matchKind === 'prefix' ? <span style={faint}> (nearest section)</span> : null}
                      {row.matchKind === 'none' ? <span style={faint}> (nothing mapped)</span> : null}
                    </div>
                    {/*
                     * TEXT. Not markup, not a link, not innerHTML — see this component's header.
                     * React escapes text content, which is why this is safe and why it has to
                     * stay exactly this shape.
                     */}
                    {row.comment ? (
                      <div className="wfb-comment" style={{ whiteSpace: 'pre-wrap', margin: '2px 0' }}>
                        {row.comment}
                      </div>
                    ) : null}
                    <div style={faint}>
                      {row.reason ? `${REASON_WORDS[row.reason] ?? row.reason} · ` : ''}
                      {row.nodeSlug ? `node ${row.nodeSlug}` : 'no node'}
                      {row.docSlug ? ` · doc ${row.docSlug}` : ''}
                      {` · ${row.locale} · ${whenText(row.createdAt)}`}
                      {row.apiKey ? ` · via ${row.apiKey.name}` : ''}
                      {row.status !== 'new' ? ` · ${row.status}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {row.status !== 'accepted' ? (
                      <button
                        className="lbtn"
                        title="I am acting on this"
                        onClick={() => void setRowStatus(row, 'accepted')}
                      >
                        Accept
                      </button>
                    ) : null}
                    {row.status !== 'dismissed' ? (
                      <button
                        className="lbtn"
                        title="Nothing to do here"
                        onClick={() => void setRowStatus(row, 'dismissed')}
                      >
                        Dismiss
                      </button>
                    ) : null}
                    {row.status !== 'new' ? (
                      <button className="lbtn" title="Put it back" onClick={() => void setRowStatus(row, 'new')}>
                        Reopen
                      </button>
                    ) : null}
                    <button
                      className="lbtn"
                      title="Delete this report for good"
                      onClick={() => void remove(row)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {data.truncated ? (
                <div style={{ ...faint, marginTop: 6 }}>
                  Showing the most recent reports. Deal with some, or delete what is done with.
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
